import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Award, Trophy, Flame, Star, ChevronRight, CheckCircle2,
  Lock, Zap, Target, RefreshCw, Crown, Gift
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BACKEND_URL, withCredentials: true, adapter: 'xhr' });

const RANK_ICONS = { Bronze: '////', Silver: '////', Gold: '////', Platinum: '////', Diamond: '////', Obsidian: '////' };

export default function Rewards() {
  const [profile, setProfile] = useState(null);
  const [quests, setQuests] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [claiming, setClaiming] = useState(null);
  const { t } = useTranslation();

  const fetchData = useCallback(async () => {
    try {
      const [p, q, l] = await Promise.all([
        api.get('/api/gamification/profile'),
        api.get('/api/gamification/quests'),
        api.get('/api/gamification/leaderboard'),
      ]);
      setProfile(p.data);
      setQuests(q.data);
      setLeaderboard(l.data.leaderboard || []);
    } catch (err) {
      console.error('Error fetching gamification:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const claimQuest = async (questId) => {
    setClaiming(questId);
    try {
      await api.post('/api/gamification/complete-quest', { quest_id: questId });
      await fetchData();
    } catch (err) {
      console.error('Error claiming quest:', err);
    }
    setClaiming(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center" data-testid="rewards-page">
        <div className="text-center">
          <Award className="w-10 h-10 text-amber-400 animate-pulse mx-auto mb-3" />
          <p className="text-slate-400 font-mono text-sm">{t('rewards.loadingRewards')}</p>
        </div>
      </div>
    );
  }

  const rank = profile?.rank || {};
  const nextRank = profile?.next_rank;
  const badges = profile?.badges || [];

  return (
    <div data-testid="rewards-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#030508]/90 backdrop-blur-xl border-b border-white/[0.08] px-4 md:px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Award className="w-5 h-5 text-amber-400" />
            <h1 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('rewards.title')}</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-amber-400/10 px-3 py-1.5 border border-amber-400/20">
              <Zap className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-amber-400 font-mono text-sm font-bold" data-testid="total-points">{profile?.points?.toLocaleString() || 0}</span>
              <span className="text-amber-400/60 text-[10px] font-mono">{t('common.pts')}</span>
            </div>
            <div className="flex items-center gap-2 bg-orange-500/10 px-3 py-1.5 border border-orange-500/20">
              <Flame className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-orange-400 font-mono text-sm font-bold" data-testid="streak-days">{profile?.streak_days || 0}</span>
              <span className="text-orange-400/60 text-[10px] font-mono">{t('common.days')}</span>
            </div>
          </div>
        </div>
      </div>

      <main className="p-4 md:p-6 space-y-6">
        {/* Rank Card */}
        <div className="bg-[#090B0F] border border-white/10 p-6" data-testid="rank-card">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-20 h-20 flex items-center justify-center border-2 relative"
                style={{ borderColor: rank.color || '#CD7F32' }}>
                <Crown className="w-8 h-8" style={{ color: rank.color || '#CD7F32' }} />
                <div className="absolute -bottom-2 px-2 py-0.5 text-[9px] font-mono font-bold tracking-wider"
                  style={{ backgroundColor: rank.color || '#CD7F32', color: '#030508' }}>
                  LVL {profile?.level || 1}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider mb-1">{t('rewards.currentRank')}</div>
                <div className="text-2xl font-black uppercase tracking-wide" style={{ color: rank.color || '#CD7F32' }} data-testid="current-rank">
                  {rank.name || 'Bronze'}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  {profile?.points?.toLocaleString()} {t('rewards.totalPoints')}
                  <span className="text-slate-600"> | </span>
                  {profile?.badges_earned_count}/{profile?.total_badges} badges
                </div>
              </div>
            </div>

            {nextRank && (
              <div className="flex-1 max-w-sm">
                <div className="flex justify-between mb-2">
                  <span className="text-[10px] font-mono text-slate-500">{t('rewards.progressTo', { rank: nextRank.name })}</span>
                  <span className="text-[10px] font-mono" style={{ color: nextRank.color }}>{profile?.progress_to_next_rank}%</span>
                </div>
                <div className="h-2 bg-[#11141D] overflow-hidden">
                  <div className="h-full transition-all duration-1000 ease-out"
                    style={{ width: `${profile?.progress_to_next_rank || 0}%`, backgroundColor: nextRank.color }} />
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-[10px] font-mono text-slate-600">{rank.min_points} pts</span>
                  <span className="text-[10px] font-mono text-slate-600">{nextRank.min_points} pts</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#090B0F] border border-white/10 p-1" data-testid="rewards-tabs">
          {[
            { id: 'overview', label: t('rewards.questsTab'), icon: Target },
            { id: 'badges', label: t('rewards.badgesTab'), icon: Award },
            { id: 'leaderboard', label: t('rewards.rankingTab'), icon: Trophy },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 font-mono text-xs transition-all ${
                activeTab === tab.id ? 'text-amber-400 bg-white/5 border-b-2 border-amber-500' : 'text-slate-500 hover:text-slate-300 border-b-2 border-transparent'
              }`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-3.5 h-3.5" />{tab.label}
            </button>
          ))}
        </div>

        {/* Quests Tab */}
        {activeTab === 'overview' && quests && (
          <div className="space-y-6" data-testid="quests-section">
            {/* Daily Quests */}
            <div className="bg-[#090B0F] border border-white/10 overflow-hidden">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-400" />
                  <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('rewards.dailyQuests')}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-slate-500">{quests.daily_progress}</span>
                  <span className="text-[10px] font-mono text-amber-400">+{quests.daily_points_available} pts available</span>
                </div>
              </div>
              <div className="divide-y divide-white/5">
                {quests.daily.map(q => (
                  <div key={q.id} className="flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors" data-testid={`quest-${q.id}`}>
                    <div className="flex items-center gap-3">
                      {q.completed
                        ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        : <div className="w-5 h-5 border border-white/20 flex items-center justify-center"><div className="w-2 h-2 bg-amber-400/30" /></div>}
                      <div>
                        <span className={`text-sm font-medium ${q.completed ? 'text-slate-500 line-through' : 'text-white'}`}>{q.name}</span>
                        <p className="text-[10px] text-slate-500">{q.desc}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-amber-400">+{q.points}</span>
                      {!q.completed && (
                        <button onClick={() => claimQuest(q.id)} disabled={claiming === q.id}
                          className="px-3 py-1 bg-amber-500/20 text-amber-400 text-[10px] font-mono font-bold hover:bg-amber-500/30 transition-colors disabled:opacity-50"
                          data-testid={`claim-${q.id}`}>
                          {claiming === q.id ? '...' : 'CLAIM'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Weekly Quests */}
            <div className="bg-[#090B0F] border border-white/10 overflow-hidden">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Gift className="w-4 h-4 text-purple-400" />
                  <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('rewards.weeklyQuests')}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-slate-500">{quests.weekly_progress}</span>
                  <span className="text-[10px] font-mono text-purple-400">+{quests.weekly_points_available} pts available</span>
                </div>
              </div>
              <div className="divide-y divide-white/5">
                {quests.weekly.map(q => (
                  <div key={q.id} className="flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors" data-testid={`quest-${q.id}`}>
                    <div className="flex items-center gap-3">
                      {q.completed
                        ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        : <div className="w-5 h-5 border border-white/20 flex items-center justify-center"><div className="w-2 h-2 bg-purple-400/30" /></div>}
                      <div>
                        <span className={`text-sm font-medium ${q.completed ? 'text-slate-500 line-through' : 'text-white'}`}>{q.name}</span>
                        <p className="text-[10px] text-slate-500">{q.desc}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-purple-400">+{q.points}</span>
                      {!q.completed && (
                        <button onClick={() => claimQuest(q.id)} disabled={claiming === q.id}
                          className="px-3 py-1 bg-purple-500/20 text-purple-400 text-[10px] font-mono font-bold hover:bg-purple-500/30 transition-colors disabled:opacity-50"
                          data-testid={`claim-${q.id}`}>
                          {claiming === q.id ? '...' : 'CLAIM'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Badges Tab */}
        {activeTab === 'badges' && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="badges-grid">
            {badges.map(b => (
              <div key={b.id}
                className={`bg-[#090B0F] border p-4 transition-all ${b.earned ? 'border-amber-500/30 hover:border-amber-500/50' : 'border-white/5 opacity-50'}`}
                data-testid={`badge-${b.id}`}>
                <div className="flex items-center justify-between mb-3">
                  {b.earned
                    ? <Award className="w-6 h-6 text-amber-400" />
                    : <Lock className="w-6 h-6 text-slate-600" />}
                  <span className="text-[10px] font-mono text-amber-400/60">+{b.points}</span>
                </div>
                <h4 className={`text-xs font-bold mb-0.5 ${b.earned ? 'text-white' : 'text-slate-600'}`}>{b.name}</h4>
                <p className="text-[10px] text-slate-500">{b.desc}</p>
                {b.earned && (
                  <div className="mt-2 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    <span className="text-[10px] font-mono text-emerald-500">{t('promoter.earned')}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="bg-[#090B0F] border border-white/10 overflow-hidden" data-testid="points-leaderboard">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-400" /> Ranking de Puntos
              </h3>
            </div>
            {leaderboard.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm font-mono">{t('rewards.noRankData')}</div>
            ) : (
              <div className="divide-y divide-white/5">
                {leaderboard.map((entry, i) => (
                  <div key={i} className="flex items-center gap-4 px-4 py-3 hover:bg-white/[0.02] transition-colors" data-testid={`rank-entry-${i}`}>
                    <span className={`text-lg font-black w-8 text-center ${i === 0 ? 'text-amber-400' : i === 1 ? 'text-slate-300' : i === 2 ? 'text-amber-700' : 'text-slate-600'}`}>
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <span className="text-sm text-white font-medium">{entry.display_name}</span>
                      <span className="text-[10px] font-mono ml-2 px-1.5 py-0.5" style={{ color: entry.rank_color, backgroundColor: `${entry.rank_color}15` }}>
                        {entry.rank}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs font-mono">
                      <span className="text-slate-400">Lvl {entry.level}</span>
                      <span className="text-orange-400">{entry.streak_days}d streak</span>
                      <span className="text-slate-400">{entry.badges_count} badges</span>
                      <span className="text-amber-400 font-bold">{entry.points?.toLocaleString()} pts</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Rank Progression Visual */}
        <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="rank-progression">
          <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white mb-4 flex items-center gap-2">
            <ChevronRight className="w-4 h-4 text-slate-500" /> {t('rewards.rankProgression')}
          </h3>
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {[
              { name: 'Bronze', pts: 0, color: '#CD7F32' },
              { name: 'Silver', pts: 500, color: '#C0C0C0' },
              { name: 'Gold', pts: '2K', color: '#FFD700' },
              { name: 'Platinum', pts: '5K', color: '#E5E4E2' },
              { name: 'Diamond', pts: '15K', color: '#B9F2FF' },
              { name: 'Obsidian', pts: '50K', color: '#0D0D0D' },
            ].map((r, i) => {
              const isActive = rank.name === r.name;
              const isPast = (profile?.points || 0) >= (typeof r.pts === 'string' ? parseInt(r.pts.replace('K', '000')) : r.pts);
              return (
                <React.Fragment key={r.name}>
                  <div className={`flex-shrink-0 w-24 border p-3 text-center transition-all ${isActive ? 'border-2 scale-105' : isPast ? 'opacity-80' : 'opacity-40'}`}
                    style={{ borderColor: isActive ? r.color : isPast ? `${r.color}60` : 'rgba(255,255,255,0.05)' }}>
                    <Crown className="w-5 h-5 mx-auto mb-1" style={{ color: r.color }} />
                    <div className="text-[10px] font-bold" style={{ color: r.color }}>{r.name}</div>
                    <div className="text-[9px] font-mono text-slate-500">{r.pts} pts</div>
                  </div>
                  {i < 5 && <div className={`w-6 h-px ${isPast ? 'bg-white/20' : 'bg-white/5'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
